/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { register } from '../registry.js';

/**
 * Paints a slider's filled track.
 *
 * The thumb's centre travels from half-a-thumb to width-minus-half-a-thumb, so a naive 0-100%
 * fill drifts away from the knob by up to half a thumb at the ends. Handing CSS a calc() that
 * mixes px with % welds the fill to the thumb centre at any container width, which is why this
 * measures nothing and needs no resize listener.
 */
export function paintSlider(slider: HTMLInputElement): void {
    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    const ratio = max === min ? 0 : (Number(slider.value) - min) / (max - min);

    slider.style.setProperty(
        '--wp-slider-progress',
        `calc(var(--wp-slider-thumb-size) / 2 + ${ratio} * (100% - var(--wp-slider-thumb-size)))`
    );
}

export function paint(root: ParentNode): void {
    root.querySelectorAll<HTMLInputElement>('[data-wp-slider]').forEach(paintSlider);
}

export function listen(): void {
    /* `input`, not `change`: `change` only fires when a drag ends, which would leave the fill
       frozen under a moving thumb. */
    document.addEventListener('input', (event) => {
        const target = event.target;

        /* `HTMLInputElement` and not `Element`: the fill is computed from `min`, `max` and
           `value`, which only a form control has. */
        if (target instanceof HTMLInputElement && target.matches('[data-wp-slider]')) {
            paintSlider(target);
        }
    });

    register(paint);
}
